import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabaseClient';
import { useResponsive } from '../hooks/useResponsive';
import { COLORS, SPACING, FONT_SIZE, BORDER_RADIUS, SHADOWS } from '../constants/theme';
import PlaceholderPlayerManager from './PlaceholderPlayerManager';
import BulkInviteTable from './BulkInviteTable';

const ParentInviteManager = ({ profile, teams }) => {
  const { isDesktop } = useResponsive();
  const [activeTab, setActiveTab] = useState('create');
  const [placeholderPlayers, setPlaceholderPlayers] = useState([]);
  const [inviteRows, setInviteRows] = useState([]);
  const [sentInvites, setSentInvites] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    loadPlaceholderPlayers();
    if (activeTab === 'view') {
      loadSentInvites();
    }
  }, [activeTab]);

  const loadPlaceholderPlayers = async () => {
    try {
      const { data, error } = await supabase
        .from('placeholder_players')
        .select('*')
        .eq('organization_id', profile.organization_id)
        .is('claimed_at', null)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setPlaceholderPlayers(data || []);
    } catch (err) {
      console.error('Error loading placeholder players:', err);
    }
  };

  const loadSentInvites = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('parent_invites')
        .select(
          `
          *,
          parent_invite_players(
            placeholder_player:placeholder_players(first_name, last_name, jersey_number)
          )
        `
        )
        .eq('organization_id', profile.organization_id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setSentInvites(data || []);
    } catch (err) {
      console.error('Error loading sent invites:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const validateInvites = () => {
    if (!inviteRows || inviteRows.length === 0) {
      return 'Please add parent information for all players';
    }

    // Check that every player has parent info (either directly or via link)
    for (const row of inviteRows) {
      // Check for errors from BulkInviteTable
      if (row.errors && Object.keys(row.errors).length > 0) {
        return 'Please fix validation errors in the invite table';
      }

      // If not linked, must have parent info
      if (!row.linked_to_player_id) {
        if (!row.parent_first_name?.trim() || !row.parent_last_name?.trim()) {
          return `Player ${row.player_name} needs parent information or must be linked to another player`;
        }
        if (!row.parent_email) {
          return `Player ${row.player_name} needs a parent email`;
        }
      }
    }

    return null;
  };

  // Group player rows by parent (either primary or linked)
  const groupRowsByParent = (rows) => {
    const families = [];
    const processed = new Set();

    rows.forEach((row) => {
      if (processed.has(row.player_id)) return;

      // Skip if linked (will be processed with parent)
      if (row.linked_to_player_id) return;

      // This is a primary parent row
      const family = {
        parent_first_name: row.parent_first_name,
        parent_last_name: row.parent_last_name,
        parent_email: row.parent_email,
        player_ids: [row.player_id],
      };

      processed.add(row.player_id);

      // Find all children linked to this parent
      rows.forEach((childRow) => {
        if (childRow.linked_to_player_id === row.player_id) {
          family.player_ids.push(childRow.player_id);
          processed.add(childRow.player_id);
        }
      });

      families.push(family);
    });

    return families;
  };

  const handleSendInvites = async () => {
    setError('');
    setSuccess('');

    // Validate
    const validationError = validateInvites();
    if (validationError) {
      setError(validationError);
      return;
    }

    // Group players by parent
    const families = groupRowsByParent(inviteRows);

    if (families.length === 0) {
      setError('No parent invites to send');
      return;
    }

    const totalPlayers = inviteRows.length;
    if (
      !window.confirm(
        `Send ${families.length} parent invite(s) for ${totalPlayers} player(s)? Emails will be sent immediately.`
      )
    ) {
      return;
    }

    try {
      setSending(true);

      let successCount = 0;
      let failCount = 0;

      // Process each family (one invite per parent)
      for (const family of families) {
        try {
          // Generate invite code
          const { data: codeData, error: codeError } = await supabase.rpc(
            'generate_parent_invite_code'
          );
          if (codeError) throw codeError;

          const code = codeData;

          // Calculate expiration (7 days from now)
          const expiresAt = new Date();
          expiresAt.setDate(expiresAt.getDate() + 7);

          // Create parent invite
          const { data: inviteData, error: inviteError } = await supabase
            .from('parent_invites')
            .insert({
              code,
              organization_id: profile.organization_id,
              created_by: profile.id, // Use profile.id instead of user.id for impersonation support
              parent_email: family.parent_email,
              parent_phone: null, // Phone removed from UI
              parent_first_name: family.parent_first_name.trim(),
              parent_last_name: family.parent_last_name.trim(),
              expires_at: expiresAt.toISOString(),
              email_status: 'pending',
              sms_status: null,
            })
            .select()
            .single();

          if (inviteError) throw inviteError;

          // Link all placeholder players in this family to the invite
          const playerLinks = family.player_ids.map((playerId) => ({
            parent_invite_id: inviteData.id,
            placeholder_player_id: playerId,
          }));

          const { error: linkError } = await supabase
            .from('parent_invite_players')
            .insert(playerLinks);

          if (linkError) throw linkError;

          // Call edge function to send email
          const { error: sendError } = await supabase.functions.invoke('send-parent-invite', {
            body: {
              parent_invite_id: inviteData.id,
              send_email: true,
              send_sms: false, // Disabled until 10DLC registration
              origin_url: window.location.origin, // Pass current origin (localhost or production)
            },
          });

          if (sendError) {
            console.error('Error sending invite:', sendError);
            failCount++;
          } else {
            successCount++;
          }
        } catch (rowError) {
          console.error('Error processing invite:', rowError);
          setError(`Error: ${rowError.message || 'Failed to process invite'}`);
          failCount++;
        }
      }

      if (successCount > 0) {
        setSuccess(
          `Successfully sent ${successCount} invite(s)!${failCount > 0 ? ` (${failCount} failed)` : ''}`
        );
        // Refresh placeholder players (some may now be assigned)
        loadPlaceholderPlayers();
        // Switch to view tab to see results
        setActiveTab('view');
      } else {
        setError('Failed to send invites. Please try again.');
      }
    } catch (err) {
      console.error('Error sending invites:', err);
      setError(err.message);
    } finally {
      setSending(false);
    }
  };

  const copyInviteLink = (code) => {
    const link = `${window.location.origin}/login?invite=${code}`;
    navigator.clipboard.writeText(link);
    setSuccess(`Invite link copied to clipboard!`);
    setTimeout(() => setSuccess(''), 3000);
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: { bg: COLORS.badge.pending.bg, text: COLORS.badge.pending.text, label: 'Pending' },
      sent: { bg: '#e3f2fd', text: '#1976d2', label: 'Sent' },
      delivered: {
        bg: COLORS.badge.completed.bg,
        text: COLORS.badge.completed.text,
        label: 'Delivered',
      },
      failed: { bg: '#ffebee', text: '#d32f2f', label: 'Failed' },
    };

    const badge = badges[status] || badges.pending;

    return (
      <span
        style={{
          backgroundColor: badge.bg,
          color: badge.text,
          padding: `${SPACING.xs} ${SPACING.sm}`,
          borderRadius: BORDER_RADIUS.sm,
          fontSize: FONT_SIZE.sm,
          fontWeight: '500',
        }}
      >
        {badge.label}
      </span>
    );
  };

  // Styles
  const containerStyle = {
    padding: SPACING.lg,
  };

  const tabsStyle = {
    display: 'flex',
    gap: SPACING.sm,
    marginBottom: SPACING.lg,
    borderBottom: `2px solid ${COLORS.gray[300]}`,
  };

  const tabStyle = (isActive) => ({
    padding: `${SPACING.md} ${SPACING.lg}`,
    fontSize: FONT_SIZE.base,
    fontWeight: '500',
    backgroundColor: 'transparent',
    border: 'none',
    borderBottom: `3px solid ${isActive ? COLORS.primary : 'transparent'}`,
    color: isActive ? COLORS.primary : COLORS.gray[600],
    cursor: 'pointer',
    marginBottom: '-2px',
  });

  const buttonStyle = {
    padding: `${SPACING.md} ${SPACING.lg}`,
    backgroundColor: COLORS.primary,
    color: COLORS.white,
    border: 'none',
    borderRadius: BORDER_RADIUS.sm,
    cursor: 'pointer',
    fontSize: FONT_SIZE.base,
    fontWeight: '500',
  };

  const sectionStyle = {
    backgroundColor: COLORS.white,
    padding: SPACING.lg,
    borderRadius: BORDER_RADIUS.md,
    boxShadow: SHADOWS.md,
    marginBottom: SPACING.lg,
  };

  const alertStyle = (type) => ({
    padding: SPACING.md,
    borderRadius: BORDER_RADIUS.sm,
    marginBottom: SPACING.lg,
    backgroundColor: type === 'error' ? COLORS.alert.error.bg : COLORS.alert.success.bg,
    color: type === 'error' ? COLORS.alert.error.text : COLORS.alert.success.text,
    border: `1px solid ${type === 'error' ? COLORS.alert.error.border : COLORS.alert.success.border}`,
  });

  const tableStyle = {
    width: '100%',
    borderCollapse: 'collapse',
    backgroundColor: COLORS.white,
  };

  const thStyle = {
    padding: SPACING.md,
    textAlign: 'left',
    borderBottom: `2px solid ${COLORS.gray[300]}`,
    fontSize: FONT_SIZE.base,
    fontWeight: '600',
    color: COLORS.gray[700],
  };

  const tdStyle = {
    padding: SPACING.md,
    borderBottom: `1px solid ${COLORS.gray[300]}`,
    fontSize: FONT_SIZE.base,
  };

  const cardStyle = {
    backgroundColor: COLORS.white,
    padding: SPACING.lg,
    borderRadius: BORDER_RADIUS.md,
    boxShadow: SHADOWS.sm,
    marginBottom: SPACING.md,
  };

  return (
    <div style={containerStyle}>
      <h1 style={{ marginTop: 0, fontSize: FONT_SIZE.xxl }}>Parent Invites</h1>

      {error && <div style={alertStyle('error')}>{error}</div>}
      {success && <div style={alertStyle('success')}>{success}</div>}

      <div style={tabsStyle}>
        <button onClick={() => setActiveTab('create')} style={tabStyle(activeTab === 'create')}>
          Create Invites
        </button>
        <button onClick={() => setActiveTab('view')} style={tabStyle(activeTab === 'view')}>
          View Sent Invites
        </button>
      </div>

      {activeTab === 'create' && (
        <>
          <div style={sectionStyle}>
            <h2 style={{ marginTop: 0 }}>Step 1: Create Placeholder Players</h2>
            <p style={{ color: COLORS.gray[600] }}>
              For players/parents that do not already have a Reps Over Rings account, you can easily
              create a player and, in Step 2, trigger a sign-up notification to their parent.
            </p>
            <PlaceholderPlayerManager
              profile={profile}
              teams={teams}
              onRefresh={loadPlaceholderPlayers}
            />
          </div>

          <div style={sectionStyle}>
            <h2 style={{ marginTop: 0 }}>Step 2: Create Parent Invites</h2>
            <p style={{ color: COLORS.gray[600] }}>
              Enter parent contact information and assign their players. Parent email is required.
            </p>
            {placeholderPlayers.length === 0 ? (
              <p style={{ color: COLORS.warning }}>
                Please create placeholder players first before sending invites.
              </p>
            ) : (
              <>
                <BulkInviteTable
                  placeholderPlayers={placeholderPlayers}
                  onInvitesChange={setInviteRows}
                />

                <div style={{ marginTop: SPACING.lg, display: 'flex', gap: SPACING.md }}>
                  <button
                    onClick={handleSendInvites}
                    disabled={sending || placeholderPlayers.length === 0}
                    style={{
                      ...buttonStyle,
                      opacity: sending || placeholderPlayers.length === 0 ? 0.5 : 1,
                      cursor:
                        sending || placeholderPlayers.length === 0 ? 'not-allowed' : 'pointer',
                    }}
                  >
                    {sending ? 'Sending...' : `Send ${inviteRows.length} Invite(s)`}
                  </button>
                </div>

                <p
                  style={{ marginTop: SPACING.md, fontSize: FONT_SIZE.sm, color: COLORS.gray[500] }}
                >
                  Note: SMS functionality requires Twilio 10DLC registration. Currently, only email
                  invites will be sent.
                </p>
              </>
            )}
          </div>
        </>
      )}

      {activeTab === 'view' && (
        <div style={sectionStyle}>
          <h2 style={{ marginTop: 0 }}>Sent Parent Invites</h2>

          {loading ? (
            <p>Loading invites...</p>
          ) : sentInvites.length === 0 ? (
            <p style={{ color: COLORS.gray[500] }}>No invites sent yet.</p>
          ) : isDesktop ? (
            <table style={tableStyle}>
              <thead>
                <tr>
                  <th style={thStyle}>Parent Name</th>
                  <th style={thStyle}>Contact</th>
                  <th style={thStyle}>Players</th>
                  <th style={thStyle}>Email Status</th>
                  <th style={thStyle}>Sent</th>
                  <th style={thStyle}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {sentInvites.map((invite) => (
                  <tr key={invite.id}>
                    <td style={tdStyle}>
                      {invite.parent_first_name} {invite.parent_last_name}
                    </td>
                    <td style={tdStyle}>
                      {invite.parent_email && <div>{invite.parent_email}</div>}
                      {invite.parent_phone && <div>{invite.parent_phone}</div>}
                    </td>
                    <td style={tdStyle}>
                      {invite.parent_invite_players.map((pip, i) => (
                        <div key={i}>
                          {pip.placeholder_player ? (
                            <>
                              {pip.placeholder_player.first_name} {pip.placeholder_player.last_name}
                              {pip.placeholder_player.jersey_number &&
                                ` #${pip.placeholder_player.jersey_number}`}
                            </>
                          ) : (
                            <em style={{ color: COLORS.gray[500] }}>Player claimed</em>
                          )}
                        </div>
                      ))}
                    </td>
                    <td style={tdStyle}>
                      {invite.email_status ? getStatusBadge(invite.email_status) : '-'}
                      {invite.email_error && (
                        <div
                          style={{
                            color: COLORS.danger,
                            fontSize: FONT_SIZE.xs,
                            marginTop: SPACING.xs,
                          }}
                        >
                          {invite.email_error}
                        </div>
                      )}
                    </td>
                    <td style={tdStyle}>
                      {invite.email_sent_at
                        ? new Date(invite.email_sent_at).toLocaleString()
                        : invite.claimed_at
                          ? 'Claimed'
                          : 'Not sent'}
                    </td>
                    <td style={tdStyle}>
                      <button
                        onClick={() => copyInviteLink(invite.code)}
                        style={{
                          ...buttonStyle,
                          padding: `${SPACING.sm} ${SPACING.md}`,
                          fontSize: FONT_SIZE.sm,
                        }}
                      >
                        Copy Link
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            // Mobile cards
            sentInvites.map((invite) => (
              <div key={invite.id} style={cardStyle}>
                <h3 style={{ margin: 0, marginBottom: SPACING.sm }}>
                  {invite.parent_first_name} {invite.parent_last_name}
                </h3>
                <p style={{ margin: `${SPACING.xs} 0` }}>
                  <strong>Contact:</strong> {invite.parent_email || invite.parent_phone}
                </p>
                <p style={{ margin: `${SPACING.xs} 0` }}>
                  <strong>Players:</strong>{' '}
                  {invite.parent_invite_players
                    .map((pip) =>
                      pip.placeholder_player
                        ? `${pip.placeholder_player.first_name} ${pip.placeholder_player.last_name}${
                            pip.placeholder_player.jersey_number
                              ? ` #${pip.placeholder_player.jersey_number}`
                              : ''
                          }`
                        : 'Player claimed'
                    )
                    .join(', ')}
                </p>
                <p style={{ margin: `${SPACING.xs} 0` }}>
                  <strong>Status:</strong>{' '}
                  {invite.email_status ? getStatusBadge(invite.email_status) : '-'}
                </p>
                <button
                  onClick={() => copyInviteLink(invite.code)}
                  style={{ ...buttonStyle, width: '100%', marginTop: SPACING.md }}
                >
                  Copy Invite Link
                </button>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};

export default ParentInviteManager;
